import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Car, Ruler, MapPin, Check, ChevronLeft, CalendarClock, Home as HomeIcon } from "lucide-react";
import Seo from "@/components/Seo";
import { useWhatsApp, WhatsAppIcon } from "@/components/Layout";
import { useSite, STATUS } from "@/context/SiteContext";
import { resolveImage } from "@/lib/api";

export default function ImovelDetalhe() {
  const { id } = useParams();
  const { properties, loadingProps, formatPrice } = useSite();
  const openWa = useWhatsApp();
  const [activeImg, setActiveImg] = useState(0);

  const property = useMemo(() => properties.find((p) => p.id === id), [properties, id]);

  if (loadingProps) {
    return (
      <div className="pt-[72px] min-h-screen flex items-center justify-center" data-testid="detail-loading">
        <div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-[72px] min-h-screen flex flex-col items-center justify-center gap-4" data-testid="detail-not-found">
        <h1 className="text-xl font-bold text-slate-800">Imóvel não encontrado</h1>
        <Link to="/imoveis" className="text-brand font-bold text-sm" data-testid="back-to-catalog-link">
          Voltar ao catálogo
        </Link>
      </div>
    );
  }

  const p = property;
  const status = STATUS[p.status] || STATUS.disponivel;
  const images = p.images?.length ? p.images : [];
  const vendido = p.status === "vendido";

  const waInterest = `Olá! Tenho interesse no imóvel ${p.title} anunciado no site da Casa Vale. Gostaria de receber mais informações.`;
  const waVisit = `Olá! Gostaria de agendar uma visita ao imóvel ${p.title} anunciado no site da Casa Vale.`;

  const metrics = [
    { icon: Ruler, label: "Área privativa", value: p.area_privativa ? `${p.area_privativa} m²` : "—" },
    { icon: HomeIcon, label: "Área total", value: p.area_total ? `${p.area_total} m²` : "—" },
    { icon: BedDouble, label: "Dormitórios", value: p.bedrooms || "—" },
    { icon: Bath, label: "Suítes", value: p.suites || "—" },
    { icon: Car, label: "Vagas", value: p.parking || "—" },
  ];

  return (
    <div className="pt-[72px]" data-testid="property-detail-page">
      <Seo
        title={`${p.title} | ${p.neighborhood ? `${p.neighborhood}, ` : ""}${p.city || "Casa Vale"}`}
        description={p.description?.slice(0, 155) || `Conheça ${p.title}, imóvel à venda pela Casa Vale Engenharia e Construção.`}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/imoveis" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-brand transition-colors" data-testid="back-link">
          <ChevronLeft className="w-4 h-4" /> Voltar ao catálogo
        </Link>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-slate-900 tracking-tight" data-testid="detail-title">
            {p.title}
          </h1>
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border ${status.cls}`} data-testid="detail-status-badge">
            {status.label}
          </span>
          {p.is_demo && (
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-slate-900 text-white" data-testid="detail-demo-badge">
              Anúncio de exemplo
            </span>
          )}
        </div>
        <p className="mt-3 text-slate-500 flex items-center gap-2" data-testid="detail-location">
          <MapPin className="w-4 h-4 text-brand" />
          {[p.neighborhood, p.city].filter(Boolean).join(" · ") || "Localização a confirmar"} ·{" "}
          {p.type === "apartamento" ? "Apartamento" : "Casa"}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-10 pb-20">
        <div className="lg:col-span-2">
          {images.length > 0 ? (
            <>
              <motion.div
                key={activeImg}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="clipped-frame overflow-hidden bg-slate-100"
                data-testid="gallery-main"
              >
                <img
                  src={resolveImage(images[activeImg])}
                  alt={`Foto ${activeImg + 1} de ${p.title}`}
                  className="w-full aspect-[16/10] object-cover"
                />
              </motion.div>
              {images.length > 1 && (
                <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="gallery-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImg(i)}
                      className={`rounded-lg overflow-hidden border-2 transition-colors duration-200 ${
                        i === activeImg ? "border-brand" : "border-transparent opacity-70 hover:opacity-100"
                      }`}
                      aria-label={`Ver foto ${i + 1}`}
                      data-testid={`gallery-thumb-${i}`}
                    >
                      <img src={resolveImage(img)} alt={`Miniatura ${i + 1} de ${p.title}`} className="w-full aspect-square object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-[16/10] bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400">
              Fotografias em breve
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-4 bg-[#FAFAFA] border border-slate-200 rounded-2xl p-6" data-testid="metrics-ribbon">
            {metrics.map((m) => (
              <div key={m.label} className="text-center">
                <m.icon className="w-5 h-5 text-brand mx-auto mb-2" />
                <p className="font-mono-num text-lg font-semibold text-slate-900">{m.value}</p>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{m.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-slate-900 tracking-tight" data-testid="description-heading">
              Sobre este imóvel
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="detail-description">
              {p.description || "Descrição em elaboração."}
            </p>
            {p.is_demo && (
              <p className="mt-4 text-xs text-slate-400 italic">
                Este é um anúncio demonstrativo utilizado apenas para apresentação do site e não corresponde a um imóvel real da Casa Vale.
              </p>
            )}
          </div>

          {p.features?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif-display text-2xl sm:text-3xl text-slate-900 tracking-tight" data-testid="features-heading">
                Características e diferenciais
              </h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="features-list">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                    <span className="w-6 h-6 rounded-full bg-brand-light flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-brand" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {p.map_url && (
            <div className="mt-10">
              <h2 className="font-serif-display text-2xl sm:text-3xl text-slate-900 tracking-tight mb-5">Localização</h2>
              <div className="rounded-2xl overflow-hidden border border-slate-200" data-testid="map-frame">
                <iframe src={p.map_url} title={`Mapa de localização de ${p.title}`} className="w-full h-80" loading="lazy" />
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 bg-white border border-slate-200 rounded-2xl shadow-lg p-7" data-testid="conversion-card">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">
            {p.finalidade === "locacao" ? "Valor de locação / mês" : "Valor de venda"}
</p>
            <p className="font-mono-num text-3xl font-semibold text-slate-900 mt-2" data-testid="detail-price">
              {formatPrice(p.price)}
            </p>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
  {p.condominio != null && (
    <p>
      <span className="font-semibold">Condomínio:</span>{" "}
      {formatPrice(p.condominio)} / mês
    </p>
  )}

  {p.iptu != null && (
    <p>
      <span className="font-semibold">IPTU:</span>{" "}
      {formatPrice(p.iptu)}
    </p>
  )}
</div>
            {vendido ? (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 text-center" data-testid="sold-banner">
                <p className="text-red-700 font-bold text-sm">Este imóvel foi vendido.</p>
                <p className="text-red-600/80 text-xs mt-1">Conheça outras opções disponíveis no catálogo.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={() => openWa(waInterest)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-6 py-3.5 rounded-full transition-colors duration-200"
                  data-testid="interest-btn"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Tenho Interesse
                </button>
                <button
                  type="button"
                  onClick={() => openWa(waVisit)}
                  className="w-full inline-flex items-center justify-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold px-6 py-3 rounded-full transition-colors duration-200"
                  data-testid="schedule-visit-btn"
                >
                  <CalendarClock className="w-5 h-5" />
                  Agendar Visita
                </button>
                <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                  Atendimento direto com a equipe da Casa Vale pelo WhatsApp.
                </p>
              </div>
            )}
            <Link
              to="/imoveis"
              className="mt-5 block text-center text-sm font-bold text-brand hover:text-brand-dark transition-colors"
              data-testid="see-more-properties-link"
            >
              Ver outros imóveis
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
